/**
 * MemberRow: one team member with the actions the caller may take on them.
 * The owner transfers or removes; a member leaves; domain members (auto-join
 * by email domain) can do neither, since auto-join would re-add them.
 */

import { Crown, LogOut, UserMinus } from 'lucide-react'

import { RowAction } from '@/components/settings/RowAction'
import { SettingsListRow } from '@/components/settings/SettingsList'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { EYEBROW } from '@/components/ui/eyebrow'
import type { TeamMember } from '@/hooks/useTeamSettings'

interface MemberRowProps {
  member: TeamMember
  displayName: string
  isSelf: boolean
  currentUserIsOwner: boolean
  /** Staff impersonation: the API refuses writes, so offer none. */
  readOnly: boolean
  busy: boolean
  onTransfer: () => void
  onRemove: () => void
  onLeave: () => void
}

/** The member's standing on the team, then "You" so your own row is always findable. */
function memberStatuses(member: TeamMember, isSelf: boolean): string[] {
  const role = member.is_owner ? 'Owner' : member.is_domain_member ? 'Domain' : null
  return [...(role === null ? [] : [role]), ...(isSelf ? ['You'] : [])]
}

export function MemberRow({
  member,
  displayName,
  isSelf,
  currentUserIsOwner,
  readOnly,
  busy,
  onTransfer,
  onRemove,
  onLeave,
}: MemberRowProps): React.JSX.Element {
  const removable = !member.is_owner && !member.is_domain_member
  return (
    <SettingsListRow>
      <Avatar className="h-7 w-7" aria-hidden="true">
        <AvatarFallback className="bg-muted text-xs text-foreground">
          {displayName.charAt(0).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm text-foreground">{displayName}</span>
        <span className="truncate font-mono text-xs text-muted-foreground">{member.email}</span>
      </span>
      {memberStatuses(member, isSelf).map((status) => (
        <span key={status} className={EYEBROW}>
          {status}
        </span>
      ))}
      {!readOnly && (
        <span className="flex items-center gap-0.5">
          {currentUserIsOwner && !member.is_owner && (
            <RowAction
              label={`Transfer ownership to ${displayName}`}
              icon={Crown}
              disabled={busy}
              onClick={onTransfer}
            />
          )}
          {currentUserIsOwner && removable && !isSelf && (
            <RowAction
              label={`Remove ${displayName}`}
              icon={UserMinus}
              destructive
              disabled={busy}
              onClick={onRemove}
            />
          )}
          {removable && isSelf && (
            <RowAction label="Leave team" icon={LogOut} destructive disabled={busy} onClick={onLeave} />
          )}
        </span>
      )}
    </SettingsListRow>
  )
}
